import {
  type ApiErrorDetail,
  ApiConnectionError,
  ApiError,
} from "../model/error/ApiClientError.model";
import { Logger } from "../utils/logger";

type Method = "GET" | "POST" | "PATCH" | "DELETE";

function buildHeaders(authToken?: string): HeadersInit {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (authToken) headers.Authorization = `Bearer ${authToken}`;
  return headers;
}

export function classifyFallbackErrorType(status: number): string {
  if (status === 401 || status === 403) return "CLIENT_AUTHORIZATION_ERROR";
  if (status === 429) return "CLIENT_RATE_LIMIT_ERROR";
  if (status >= 400 && status <= 499) return "CLIENT_REQUEST_ERROR";
  if (status >= 500 && status <= 599) return "SERVER_TRANSIENT_ERROR";
  return "CLIENT_INTERNAL_ERROR";
}

function isErrorsBody(value: unknown): value is { errors: ApiErrorDetail[] } {
  if (typeof value !== "object" || value === null || !("errors" in value)) return false;
  const { errors } = value as { errors: unknown };
  return (
    Array.isArray(errors) &&
    errors.length > 0 &&
    errors.every(
      (entry: unknown) =>
        typeof entry === "object" &&
        entry !== null &&
        typeof (entry as ApiErrorDetail).type === "string" &&
        typeof (entry as ApiErrorDetail).detail === "string",
    )
  );
}

async function buildApiError(method: Method, url: string, response: Response): Promise<ApiError> {
  let body: unknown;
  let parseError: unknown;
  try {
    body = await response.json();
  } catch (cause) {
    parseError = cause;
  }

  const errors: ApiErrorDetail[] = isErrorsBody(body)
    ? body.errors
    : [
        {
          type: classifyFallbackErrorType(response.status),
          detail: `${response.status} ${response.statusText}`,
        },
      ];

  return new ApiError({ url, method, status: response.status, errors, cause: parseError });
}

export async function request(
  method: Method,
  url: string,
  authToken?: string,
  body?: object,
): Promise<Response> {
  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers: buildHeaders(authToken),
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (cause) {
    throw new ApiConnectionError(`Network request failed: ${method} ${url}`, cause);
  }
  if (!response.ok) {
    throw await buildApiError(method, url, response);
  }
  return response;
}

export async function retryOnConnectionError<T>(
  fn: () => Promise<T>,
  maxRetries = 1,
  delayMs = 1000,
): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    Logger.logError(error, `Fetch failed. Retries left: ${maxRetries}`);
    if (maxRetries <= 0 || !(error instanceof ApiConnectionError)) {
      throw error;
    }
    await new Promise((resolve) => setTimeout(resolve, delayMs));
    return retryOnConnectionError(fn, maxRetries - 1, delayMs * 2);
  }
}
